import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Target, AlertCircle, ArrowRight } from 'lucide-react';

interface AIInsightsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const mockInsights = [
  {
    id: '1',
    type: 'priority-collection',
    icon: TrendingUp,
    title: 'Top Priority Collection',
    description: 'Manufacturing Co Ltd',
    detail: 'High payment probability based on historical patterns',
    action: 'Initiate Collection Call',
    actionUrl: '/customers/manufacturing-co',
    impact: {
      expectedRecovery: '£505,000'
    },
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600'
  },
  {
    id: '2',
    type: 'optimization',
    icon: Target,
    title: 'Optimization Tip',
    description: 'Focus on customers with 60-90 day aging for best recovery rates this week',
    detail: 'Historical data shows 78% success rate in this aging band',
    action: 'View aging analysis',
    actionUrl: '/analytics-reporting/ageing-analysis',
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600'
  },
  {
    id: '3',
    type: 'risk-alert',
    icon: AlertCircle,
    title: 'Risk Alert',
    description: '3 customers showing payment pattern deterioration',
    detail: 'Average payment days increased from 30 to 52 days',
    action: 'Review customers',
    actionUrl: '/customers?filter=risk',
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600'
  }
];

export function AIInsightsModal({ isOpen, onClose }: AIInsightsModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">✨</span>
            AI Insights
            <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
              89% confidence
            </span>
          </DialogTitle>
          <p className="text-sm text-gray-600">
            Machine learning recommendations based on your portfolio data
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {mockInsights.map((insight) => {
            const Icon = insight.icon;
            return (
              <Card key={insight.id} className={insight.bgColor}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg bg-white ${insight.iconColor}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {insight.title}
                      </h3>
                      <p className="text-sm text-gray-700 mb-1">
                        {insight.description}
                      </p>
                      <p className="text-xs text-gray-600 mb-3">
                        {insight.detail}
                      </p>
                      
                      {insight.impact && (
                        <div className="mb-3 p-2 bg-white rounded border border-gray-200">
                          <p className="text-xs text-gray-600">Expected Recovery</p>
                          <p className="text-lg font-bold text-gray-900">
                            {insight.impact.expectedRecovery}
                          </p>
                        </div>
                      )}

                      <Button
                        onClick={() => {
                          // Navigate to action URL
                          window.location.href = insight.actionUrl;
                        }}
                        className="w-full gap-2"
                      >
                        {insight.action}
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
